import React, { useCallback, useEffect, useRef, useState } from 'react';
import { isEqual } from 'lodash';

import { UnitAssumption } from '@futrli-sdk/predict';
import { AnyObject, useCrudActivityContext } from '@futrli/shared-services';

import { AllowedAssumptionTypes } from '../../../../detail/reports/actions/actions';
import { getUnitMethodData, getPriceVariantsWithStrDate } from './utils';
import { PaymentTreatment, PredictionType } from '../../../types';
import { PredictionEditor } from '../prediction-editor/prediction-editor';
import { PredictionHeader } from '../prediction-header/prediction-header';
import { TRACKING_EVENTS } from '../../../../../core/constants';
import { trackEvent } from '../../../../../core/tracker';
import { UnitMethod, UnitMethodData, getInitialPriceVariant } from '../../../components/unit/unit-method';
import { useStandardOrganisationPredictionBuilderContext } from '../../../context/standard-organisation-prediction-builder-context';
import { usePredictManagementContext } from '../../../../../predict-management/context/predict-management-context';

interface Props {
  onClose?: () => void;
  isPredictionTypeActive?: boolean;
}

export const UnitPrediction = ({ onClose, isPredictionTypeActive }: Props) => {
  const {
    accountIndex,
    handleSaveExisting,
    handleSaveNew,
    isCashFlowDisabled,
    isEditing,
    isQuickCopy,
    paymentDataSaveParams,
    paymentTreatment,
    predictionData,
    predictionName,
    predictionType,
    reloadForecastData,
    reset,
    resetUrlData,
    setImpactPreviewParams,
    setIsSaveDisabled,
    setPredictionContentTouched,
    urlPredictionId,
    vatPercentage,
    previewImpactsResponse
  } = useStandardOrganisationPredictionBuilderContext();

  const { addCreating, removeCreating } = useCrudActivityContext();
  const { currency } = usePredictManagementContext();

  const [data, setData] = useState<UnitMethodData>({ price_variants: getInitialPriceVariant() });
  const [validData, setValidData] = useState<UnitMethodData>();
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [isDataValid, setIsDataValid] = useState(false);

  const isActive = predictionType === PredictionType.UNIT;
  const isFirstRun = useRef(true);
  const lastImpactPreviewParams = useRef<Partial<AllowedAssumptionTypes>>();

  // *********
  // * Utils *
  // *********

  const refreshAccountsImpactPreview = useCallback(async () => {
    if (accountIndex && !isCashFlowDisabled && validData) {
      const newParams = getUnitMethodData({
        predictionName: 'impact-data-request', // Dont use real predictionName here as it causes unneccesary refreshes
        vatPercentage,
        unitMethodData: validData,
        accountIndex,
        paymentTreatment
      });

      if (!isEqual(newParams, lastImpactPreviewParams.current)) {
        lastImpactPreviewParams.current = newParams;
        setImpactPreviewParams(newParams);
      }
    }
  }, [accountIndex, isCashFlowDisabled, validData, vatPercentage, paymentTreatment, setImpactPreviewParams]);

  const restoreInitialContent = useCallback(() => {
    setData({ price_variants: getInitialPriceVariant() });
    setIsDataValid(false);
    return true;
  }, []);

  const restoreLoadedContent = useCallback(() => {
    if (predictionData) {
      setData({
        price_variants: getInitialPriceVariant((predictionData as UnitAssumption).price_variants)
      });
      return true;
    }
    return false;
  }, [predictionData]);

  const save = useCallback(async () => {
    const params = {
      ...data,
      price_variants: getPriceVariantsWithStrDate(data.price_variants),
      name: predictionName,
      vat_percentage: paymentTreatment === PaymentTreatment.NON_CASH_TRANSFER ? '0' : vatPercentage || null,
      account_id: accountIndex,
      excluded_from_report: false,
      amount: '0',
      invoice_date: null,
      payment_type_data: paymentDataSaveParams
    };

    let success = false;
    if (urlPredictionId && data && !isQuickCopy) {
      success = await handleSaveExisting(params);
    } else {
      success = !!(await handleSaveNew(params));
      if (success) {
        trackEvent(TRACKING_EVENTS.CREATED_ANY_MANUAL_PREDICTION);
        trackEvent(TRACKING_EVENTS.CREATE_PREDICTION_UNIT_NEW);
      }
    }

    setIsFormSubmitted(success);
    return success;
  }, [
    accountIndex,
    data,
    handleSaveExisting,
    handleSaveNew,
    isQuickCopy,
    paymentDataSaveParams,
    paymentTreatment,
    predictionName,
    urlPredictionId,
    vatPercentage
  ]);

  // ************
  // * Handlers *
  // ************

  const handleContentChange = useCallback(
    (isValid: boolean, data: AnyObject) => {
      setPredictionContentTouched(true);
      setIsFormSubmitted(false);
      const newData = (data as unknown) as UnitMethodData;
      setData(newData);
      setIsDataValid(isValid);
      if (isValid) {
        setValidData(newData);
      }
    },
    [setPredictionContentTouched]
  );

  const handleSave = useCallback(async () => {
    addCreating('unit-prediction');
    const success = await save();
    if (success) {
      reloadForecastData();
      onClose?.();
    }
    removeCreating('unit-prediction');
  }, [addCreating, onClose, reloadForecastData, removeCreating, save]);

  const handleSaveAndNew = useCallback(async () => {
    addCreating('unit-prediction');
    const success = await save();
    if (success) {
      reloadForecastData();
      reset();
      resetUrlData();
      restoreInitialContent();
    }
    removeCreating('unit-prediction');
  }, [addCreating, reloadForecastData, removeCreating, reset, restoreInitialContent, save, resetUrlData]);

  // ***********
  // * Effects *
  // ***********

  // Set initial / restore saved content on first run
  useEffect(() => {
    if (isFirstRun.current) {
      const contentWasRestored =
        (isEditing || isQuickCopy) && isActive ? restoreLoadedContent() : restoreInitialContent();
      if (contentWasRestored) {
        isFirstRun.current = false;
      }
    }
  }, [isActive, isEditing, restoreInitialContent, restoreLoadedContent, isQuickCopy]);

  // Refresh the accounts impacted info
  useEffect(() => {
    if (isActive) {
      refreshAccountsImpactPreview();
    } else {
      lastImpactPreviewParams.current = undefined;
    }
  }, [isActive, refreshAccountsImpactPreview]);

  // Update isSaveDisabled flag
  useEffect(() => {
    if (isActive) {
      setIsSaveDisabled(!isDataValid);
    }
  }, [isActive, isDataValid, setIsSaveDisabled]);

  return (
    <PredictionEditor
      onSave={handleSave}
      onSaveAndNew={handleSaveAndNew}
      show={isActive}
      onClose={onClose}
      isPredictionTypeActive={isPredictionTypeActive}
    >
      <UnitMethod
        currency={currency!}
        initialPriceVariants={data.price_variants}
        isFormSubmitted={isFormSubmitted}
        onChange={handleContentChange}
        PredictionHeader={PredictionHeader}
        previewImpacts={previewImpactsResponse}
      />
    </PredictionEditor>
  );
};
